import { alignmentsAtom } from '@/atoms/alignment.atom';
import { layersAtom } from '@/atoms/layers.atom';
import { stationVolumeDockAtom } from '@/atoms/stationVolumes.atom';
import { useClearance } from '@/hooks/useClearance';
import { useFeature } from '@/hooks/useFeature';
import { clearanceBandSource } from '@/lib/clearance/bands';
import { alignmentHashMatches } from '@/lib/stationVolumes/alignmentInput';
import { bandPresentation } from '@/lib/stationVolumes/bandGeometry';
import { useSvPalette } from '@/lib/stationVolumes/useSvPalette';
import { useAtomValue } from 'jotai';

/**
 * Everything the scene needs to draw a FINISHED clearance run: gate, then the
 * dock's DATASET, then the layer's metadata, then the bins JSON, then the
 * alignment and the freshness verdict.
 *
 * The first three steps are pure and live in `clearanceBandSource`
 * (`lib/clearance/bands.ts`), where they are exhaustively tested — a locked
 * user or an open volumes dock collapses everything downstream in one step, so
 * nothing renders AND nothing is fetched (the bins JSON is multi-megabyte).
 *
 * It has a module of its own rather than living beside its consumer because it
 * used to live in `ClearanceBands.tsx`, and that component is gone: the flat
 * corridor plates and the swept envelope were two objects occupying the same
 * plane at the alignment datum, and the envelope absorbed the plates' verdict
 * colour, selection and hover. A hook exported from a component the scene no
 * longer mounts is exactly the import cycle waiting to happen that
 * `lib/three/unitsPerPixel.ts`'s docstring warns about.
 */
export function useClearanceRunSource() {
  const gateAllowed = useFeature('analysis.clearance').allowed;
  const palette = useSvPalette();
  const openDock = useAtomValue(stationVolumeDockAtom);
  const layers = useAtomValue(layersAtom);
  const alignments = useAtomValue(alignmentsAtom);

  const { dock, meta, batchId } = clearanceBandSource(gateAllowed, openDock, layers);
  const { data: json } = useClearance(meta, batchId);
  const alignment = meta ? alignments.get(meta.alignment.key) : undefined;
  // Data honesty (A8): the envelope is drawn on the CURRENT centreline, so when
  // the registry alignment no longer hashes to the one the run measured, the
  // corridor on screen is not the corridor the numbers came from. Same
  // predicate the dock's amber chip uses.
  const alignmentMatches = meta ? alignmentHashMatches(meta.alignment, alignment) : true;
  return {
    dock,
    json,
    meta,
    batchId,
    alignment,
    alignmentMatches,
    palette,
    // Only `silhouette` is read now — it is the shared "stale run is shown, but
    // qualified in amber" decision (`bandPresentation`), kept in one place so
    // the clearance envelope and the volumes bands cannot disagree about when a
    // run is stale. `opacity` is the plates' figure and no longer has a reader.
    presentation: bandPresentation(alignmentMatches, palette),
  };
}
